<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container feedback-detail-modal">
      <div class="modal-header">
        <div class="header-content">
          <div class="type-badge" :class="feedback.feedback_type">
            <i :class="getTypeIcon(feedback.feedback_type)"></i>
            {{ feedback.feedback_type_display }}
          </div>
          <div class="status-badge" :class="feedback.status">
            {{ feedback.status_display }}
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Title & Meta -->
        <div class="feedback-header">
          <h2>{{ feedback.title }}</h2>
          <div class="meta-row">
            <span class="app-tag" :class="feedback.application">
              {{ feedback.application_display }}
            </span>
            <span class="priority-badge" :class="feedback.priority">
              {{ feedback.priority }}
            </span>
            <span>
              <i class="fas fa-user"></i>
              {{ feedback.user_name || 'Unknown' }}
            </span>
            <span>
              <i class="fas fa-clock"></i>
              {{ formatDate(feedback.created_at) }}
            </span>
          </div>
        </div>
        
        <!-- Description -->
        <div class="section">
          <h3>Description</h3>
          <p class="description">{{ feedback.description }}</p>
        </div>
        
        <!-- Bug Details (if applicable) -->
        <template v-if="feedback.feedback_type === 'bug'">
          <div v-if="feedback.steps_to_reproduce" class="section">
            <h3>Steps to Reproduce</h3>
            <pre class="steps">{{ feedback.steps_to_reproduce }}</pre>
          </div>
          
          <div class="section-row" v-if="feedback.expected_behavior || feedback.actual_behavior">
            <div v-if="feedback.expected_behavior" class="section">
              <h3>Expected Behavior</h3>
              <p>{{ feedback.expected_behavior }}</p>
            </div>
            <div v-if="feedback.actual_behavior" class="section">
              <h3>Actual Behavior</h3>
              <p>{{ feedback.actual_behavior }}</p>
            </div>
          </div>
        </template>
        
        <!-- Context Info -->
        <div class="section context-section">
          <h3><i class="fas fa-info-circle"></i> Context</h3>
          <div class="context-grid">
            <div class="context-item">
              <label>Page URL</label>
              <code>{{ feedback.page_url }}</code>
            </div>
            <div class="context-item">
              <label>Version</label>
              <span>{{ feedback.app_version }}</span>
            </div>
            <div class="context-item">
              <label>Browser</label>
              <span>{{ truncate(feedback.browser_info, 60) }}</span>
            </div>
            <div class="context-item">
              <label>Screen</label>
              <span>{{ feedback.screen_resolution }}</span>
            </div>
          </div>
          
          <!-- Console Errors - Only shown to platform users (technical debugging info) -->
          <div v-if="showTechnicalDetails && feedback.console_errors?.length" class="errors-section">
            <label>Console Errors</label>
            <div class="error-list">
              <div v-for="(error, i) in feedback.console_errors" :key="i" class="error-item">
                <span class="error-time">{{ error.timestamp }}</span>
                <span class="error-message">{{ error.message }}</span>
              </div>
            </div>
          </div>
          
          <!-- Network Failures - Only shown to platform users (technical debugging info) -->
          <div v-if="showTechnicalDetails && feedback.network_failures?.length" class="errors-section">
            <label>Network Failures</label>
            <div class="error-list">
              <div v-for="(failure, i) in feedback.network_failures" :key="i" class="error-item">
                <span class="error-time">{{ failure.timestamp }}</span>
                <span class="error-message">{{ failure.url }} - {{ failure.status || failure.error }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Comments -->
        <div class="section comments-section">
          <h3><i class="fas fa-comments"></i> Comments ({{ feedback.comments?.length || 0 }})</h3>
          
          <div v-if="feedback.comments?.length" class="comments-list">
            <div v-for="comment in feedback.comments" :key="comment.id" class="comment">
              <div class="comment-header">
                <span class="comment-author">{{ comment.user_name }}</span>
                <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
                <span v-if="comment.new_status" class="status-change">
                  → {{ comment.new_status }}
                </span>
              </div>
              <p class="comment-body">{{ comment.comment }}</p>
            </div>
          </div>
          
          <!-- Add Comment -->
          <div class="add-comment">
            <textarea 
              v-model="newComment" 
              placeholder="Add a comment..."
              rows="2"
            ></textarea>
            <div class="comment-actions">
              <select v-model="newStatus">
                <option value="">No status change</option>
                <option value="acknowledged">Mark Acknowledged</option>
                <option value="in_progress">Mark In Progress</option>
                <option value="resolved">Mark Resolved</option>
                <option value="wont_fix">Won't Fix</option>
              </select>
              <button 
                class="btn btn-primary"
                @click="addComment"
                :disabled="!newComment.trim() || submitting"
              >
                <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                <span v-else>Add Comment</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Quick Actions -->
      <div class="modal-footer">
        <button 
          v-if="feedback.status !== 'resolved'"
          class="btn btn-success"
          @click="markResolved"
          :disabled="resolving"
        >
          <i class="fas fa-check"></i>
          Mark Resolved
        </button>
        <button class="btn btn-secondary" @click="$emit('close')">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import observabilityAPI from '@/services/observabilityAPI';
import authService from '@/services/auth';

export default {
  name: 'FeedbackDetailModal',
  
  props: {
    feedback: {
      type: Object,
      required: true,
    },
  },
  
  emits: ['close', 'updated'],
  
  data() {
    return {
      newComment: '',
      newStatus: '',
      submitting: false,
      resolving: false,
    };
  },
  
  computed: {
    /**
     * Only show technical debugging info (console errors, network failures)
     * to platform users (Praetorian AI staff). Regular users won't understand
     * these technical details and it may confuse them.
     */
    showTechnicalDetails() {
      return authService.hasPlatformAccess();
    },
  },
  
  methods: {
    getTypeIcon(type) {
      const icons = {
        bug: 'fas fa-bug',
        feature: 'fas fa-lightbulb',
        feedback: 'fas fa-comment',
        question: 'fas fa-question-circle',
      };
      return icons[type] || 'fas fa-comment';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    
    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    
    async addComment() {
      if (!this.newComment.trim()) return;
      
      this.submitting = true;
      try {
        await observabilityAPI.addFeedbackComment(
          this.feedback.id,
          this.newComment,
          this.newStatus || null
        );
        
        // Reload feedback to get updated comments
        const updated = await observabilityAPI.getFeedbackDetail(this.feedback.id);
        this.$emit('updated', updated);
        
        this.newComment = '';
        this.newStatus = '';
      } catch (error) {
        console.error('Failed to add comment:', error);
      } finally {
        this.submitting = false;
      }
    },
    
    async markResolved() {
      this.resolving = true;
      try {
        const updated = await observabilityAPI.resolveFeedback(this.feedback.id);
        this.$emit('updated', updated);
      } catch (error) {
        console.error('Failed to resolve feedback:', error);
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.feedback-detail-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.type-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.type-badge.bug {
  background: #fed7d7;
  color: #c53030;
}

.type-badge.feature {
  background: #fefcbf;
  color: #d69e2e;
}

.type-badge.feedback {
  background: #c6f6d5;
  color: #276749;
}

.type-badge.question {
  background: #bee3f8;
  color: #2b6cb0;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.new { background: #ebf4ff; color: #667eea; }
.status-badge.acknowledged { background: #e9d8fd; color: #6b46c1; }
.status-badge.in_progress { background: #fefcbf; color: #d69e2e; }
.status-badge.resolved { background: #c6f6d5; color: #276749; }
.status-badge.wont_fix { background: #e2e8f0; color: #4a5568; }

.close-btn {
  background: transparent;
  border: none;
  color: #718096;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e2e8f0;
  color: #4a5568;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.feedback-header h2 {
  margin: 0 0 12px;
  font-size: 20px;
  color: #2d3748;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #718096;
}

.meta-row span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.app-tag {
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 500;
}

.app-tag.adi { background: #ebf4ff; color: #3182ce; }
.app-tag.lms { background: #fef3c7; color: #92400e; }
.app-tag.couples { background: #fce7f3; color: #9d174d; }
.app-tag.platform { background: #e2e8f0; color: #4a5568; }

.priority-badge {
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: capitalize;
}

.priority-badge.low { background: #f0fff4; color: #276749; }
.priority-badge.medium { background: #fefcbf; color: #744210; }
.priority-badge.high { background: #fed7d7; color: #c53030; }
.priority-badge.critical { background: #742a2a; color: white; }

.section {
  margin-top: 20px;
}

.section h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section h3 i {
  font-size: 13px;
  color: #718096;
}

.description {
  margin: 0;
  font-size: 14px;
  color: #2d3748;
  line-height: 1.6;
  white-space: pre-wrap;
}

.steps {
  margin: 0;
  padding: 12px;
  background: #f7fafc;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  white-space: pre-wrap;
  color: #4a5568;
}

.section-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.context-section {
  background: #f7fafc;
  padding: 16px;
  border-radius: 8px;
  margin-top: 20px;
}

.context-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}

.context-item label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #718096;
  margin-bottom: 4px;
}

.context-item code,
.context-item span {
  font-size: 12px;
  color: #4a5568;
}

.context-item code {
  background: #edf2f7;
  padding: 2px 6px;
  border-radius: 4px;
  word-break: break-all;
}

.errors-section {
  margin-top: 16px;
}

.error-list {
  margin-top: 8px;
  max-height: 150px;
  overflow-y: auto;
}

.error-item {
  font-size: 11px;
  padding: 6px;
  background: #fed7d7;
  border-radius: 4px;
  margin-bottom: 4px;
}

.error-time {
  color: #718096;
  margin-right: 8px;
}

.error-message {
  color: #c53030;
}

.comments-section {
  margin-top: 24px;
}

.comments-list {
  margin-top: 12px;
}

.comment {
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  margin-bottom: 8px;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
}

.comment-author {
  font-weight: 600;
  color: #2d3748;
}

.comment-date {
  color: #718096;
}

.status-change {
  background: #e9d8fd;
  color: #6b46c1;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.comment-body {
  margin: 0;
  font-size: 14px;
  color: #4a5568;
}

.add-comment {
  margin-top: 16px;
}

.add-comment textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
}

.add-comment textarea:focus {
  outline: none;
  border-color: #667eea;
}

.comment-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.comment-actions select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-primary:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.btn-success {
  background: #38a169;
  color: white;
}

.btn-success:hover {
  background: #2f855a;
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f7fafc;
}

@media (max-width: 600px) {
  .context-grid,
  .section-row {
    grid-template-columns: 1fr;
  }
}
</style>
